import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { HelpTrigger } from '../../../help';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle, Pencil, Plus, Trash2 } from 'lucide-react';
import { OpenExternalIcon } from '../../../components/icons';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import AdminModal from '../../components/AdminModal';
import AdminPageShell from '../../components/AdminPageShell';
import AdminTabs from '../../components/AdminTabs';
import FieldList from '../../components/FieldList';
import EntityAuditList from '../../components/EntityAuditList';
import CandidateProfileCoverageCard from './CandidateProfileCoverageCard';
import CycleMilestonesCard, { type CycleMilestone } from './CycleMilestonesCard';
import ElectionResultsTab from './ElectionResultsTab';
import {
  CONTEST_ELECTION_TYPES,
  CONTEST_PARTY_OPTIONS,
  CONTEST_STATUSES,
  CYCLE_ELECTION_TYPES,
} from '../../../constants/elections';
import { formatDate } from '../../lib/format';

interface CycleDetail {
  id: string;
  state: string;
  election_year: number;
  election_type: string;
  name: string | null;
  notes: string | null;
  cycle_start_date: string | null;
  cycle_end_date: string | null;
  blue_book_url: string | null;
  created_at: string;
  milestones: CycleMilestone[];
}

interface Contest {
  id: string;
  display_label: string | null;
  election_type: string;
  party: string | null;
  status: string;
  office_id: string;
  office_key: string | null;
  district_display: string | null;
  candidate_count: number;
}

interface CatalogOffice {
  id: string;
  display_label: string;
  scope: string | null;
  district_scope: string | null;
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'results', label: 'Results' },
  { key: 'activity', label: 'Activity' },
];

const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';

const EMPTY_CONTEST_FORM = {
  id: '',
  office_id: '',
  electoral_district_id: '',
  election_type: 'general',
  party: '',
  display_label: '',
  status: 'expected',
};

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

/**
 * Election cycle detail — the cycle's identity + statutory window, its
 * milestones (shared by every candidate in the jurisdiction), and its audit
 * trail. Contest management lands here in E3 (ELECTIONS_TOOL_PLAN.md).
 */
const ElectionCycleDetailPage: React.FC = () => {
  const { calendarId } = useParams<{ calendarId: string }>();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '', election_type: 'regular', notes: '', cycle_start_date: '', cycle_end_date: '', blue_book_url: '',
  });
  const [contestModal, setContestModal] = useState<'add' | 'edit' | null>(null);
  const [contestForm, setContestForm] = useState(EMPTY_CONTEST_FORM);
  const [deleteContestTarget, setDeleteContestTarget] = useState<{ id: string; label: string } | null>(null);

  const cycleQueryKey = ['admin-elections', 'cycle', calendarId] as const;
  const detail = useQuery({
    queryKey: cycleQueryKey,
    queryFn: async () => {
      const response = await api.get(`/election-calendars/${calendarId}`);
      return response.data.data as CycleDetail;
    },
    enabled: !!calendarId,
  });

  const contests = useQuery({
    queryKey: ['admin-elections', 'cycle-contests', calendarId],
    queryFn: async () => {
      const response = await api.get(`/election-calendars/${calendarId}/contests`);
      return (response.data.data ?? []) as Contest[];
    },
    enabled: !!calendarId,
  });

  const offices = useQuery({
    queryKey: ['admin-elections', 'offices'],
    queryFn: async () => {
      const response = await api.get('/contests/offices');
      return (response.data.data ?? []) as CatalogOffice[];
    },
    staleTime: 5 * 60 * 1000,
    enabled: contestModal === 'add',
  });

  const selectedOffice = (offices.data ?? []).find((office) => office.id === contestForm.office_id);

  const districts = useQuery({
    queryKey: ['admin-elections', 'districts', selectedOffice?.district_scope ?? ''],
    queryFn: async () => {
      const response = await api.get('/contests/districts', {
        params: { scope: selectedOffice?.district_scope },
      });
      return (response.data.data ?? []) as Array<{ id: string; display_label: string }>;
    },
    staleTime: 5 * 60 * 1000,
    enabled: contestModal === 'add' && selectedOffice?.scope === 'by_district' && !!selectedOffice?.district_scope,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-elections', 'cycle', calendarId] });
    queryClient.invalidateQueries({ queryKey: ['admin-elections', 'cycles'] });
    queryClient.invalidateQueries({ queryKey: ['admin-elections', 'cycle-contests', calendarId] });
    queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'audit', 'election_calendar', calendarId ?? ''] });
  };

  const editMutation = useMutation({
    mutationFn: () =>
      api.put(`/election-calendars/${calendarId}`, {
        name: editForm.name.trim() || undefined,
        election_type: editForm.election_type,
        notes: editForm.notes.trim() || undefined,
        cycle_start_date: editForm.cycle_start_date || undefined,
        cycle_end_date: editForm.cycle_end_date || undefined,
        // Always sent: blank clears the cycle's Blue Book link.
        blue_book_url: editForm.blue_book_url.trim(),
      }),
    onSuccess: () => {
      toast.success('Cycle updated');
      setEditOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to update cycle')),
  });

  const contestSaveMutation = useMutation({
    mutationFn: () => {
      if (contestModal === 'add') {
        return api.post(`/election-calendars/${calendarId}/contests`, {
          office_id: contestForm.office_id,
          electoral_district_id:
            selectedOffice?.scope === 'by_district' ? contestForm.electoral_district_id : undefined,
          election_type: contestForm.election_type,
          party: contestForm.election_type === 'primary' ? contestForm.party || null : null,
          display_label: contestForm.display_label.trim() || undefined,
        });
      }
      return api.put(`/election-calendars/${calendarId}/contests/${contestForm.id}`, {
        display_label: contestForm.display_label.trim() || null,
        election_type: contestForm.election_type,
        party: contestForm.election_type === 'primary' ? contestForm.party || null : null,
        status: contestForm.status,
      });
    },
    onSuccess: () => {
      toast.success(contestModal === 'add' ? 'Contest added' : 'Contest updated');
      setContestModal(null);
      setContestForm(EMPTY_CONTEST_FORM);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save contest')),
  });

  const markOfficialMutation = useMutation({
    mutationFn: (contestId: string) =>
      api.put(`/election-calendars/${calendarId}/contests/${contestId}`, { status: 'official' }),
    onSuccess: () => {
      toast.success('Contest marked official');
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to update contest')),
  });

  const contestDeleteMutation = useMutation({
    mutationFn: (contestId: string) =>
      api.delete(`/election-calendars/${calendarId}/contests/${contestId}`),
    onSuccess: () => {
      toast.success('Contest deleted — its candidates were unlinked, not deleted');
      setDeleteContestTarget(null);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to delete contest')),
  });

  if (detail.isLoading) {
    return (
      <AdminPageShell title="Election Cycle" backUrl="/admin-next/elections">
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading cycle…</p>
      </AdminPageShell>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <AdminPageShell title="Election Cycle" backUrl="/admin-next/elections">
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">Couldn't load this cycle.</p>
          <Button variant="outline" size="sm" onClick={() => detail.refetch()}>Retry</Button>
        </div>
      </AdminPageShell>
    );
  }

  const cycle = detail.data;

  return (
    <AdminPageShell
      title={cycle.name || `${cycle.election_year} ${cycle.state} ${cycle.election_type}`}
      description="Election cycle"
      backUrl="/admin-next/elections"
      actions={
        <Button variant="outline" size="sm" icon={Pencil}
          onClick={() => {
            setEditForm({
              name: cycle.name ?? '',
              election_type: cycle.election_type,
              notes: cycle.notes ?? '',
              cycle_start_date: cycle.cycle_start_date ? cycle.cycle_start_date.slice(0, 10) : '',
              cycle_end_date: cycle.cycle_end_date ? cycle.cycle_end_date.slice(0, 10) : '',
              blue_book_url: cycle.blue_book_url ?? '',
            });
            setEditOpen(true);
          }}>
          Edit Cycle
        </Button>
      }
    >
      <AdminTabs tabs={TABS} activeKey={tab} onChange={setTab} />

      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="card p-5">
            <FieldList
              fields={[
                { label: 'ID', value: <span className="font-mono text-xs">{cycle.id}</span>, copyValue: cycle.id },
                { label: 'State', value: cycle.state },
                { label: 'Election Year', value: String(cycle.election_year) },
                { label: 'Type', value: cycle.election_type },
                {
                  label: 'Cycle Window',
                  value:
                    cycle.cycle_start_date && cycle.cycle_end_date
                      ? `${formatDate(cycle.cycle_start_date)} – ${formatDate(cycle.cycle_end_date)}`
                      : '—',
                },
                {
                  label: 'Blue Book',
                  value: cycle.blue_book_url ? (
                    <a href={cycle.blue_book_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 break-all text-primary-600 hover:underline dark:text-primary-400">
                      {cycle.blue_book_url}
                      <OpenExternalIcon className="h-3 w-3 shrink-0" aria-hidden />
                    </a>
                  ) : (
                    <span className="text-text-tertiary dark:text-text-dark-tertiary">
                      Not set — measures in this cycle show no Blue Book link unless they carry their own
                    </span>
                  ),
                  copyValue: cycle.blue_book_url ?? undefined,
                },
                { label: 'Notes', value: cycle.notes ?? '—' },
                { label: 'Created', value: formatDate(cycle.created_at) },
              ]}
            />
          </div>

          <CycleMilestonesCard
            cycleId={cycle.id}
            milestones={cycle.milestones}
            cycleQueryKey={cycleQueryKey}
            onChanged={invalidate}
          />

          <div className="card p-5">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-1.5">
                <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Contests</h4>
                <HelpTrigger contentKey="primary-to-general" size="sm" ariaLabel="How primaries, generals, and advancing winners work" />
              </span>
              <Button variant="ghost" size="sm" icon={Plus}
                onClick={() => { setContestForm(EMPTY_CONTEST_FORM); setContestModal('add'); }}>
                Add Contest
              </Button>
            </div>
            {contests.isError ? (
              <p className="text-sm text-error-600 dark:text-error-400">Couldn't load contests.</p>
            ) : (contests.data ?? []).length === 0 ? (
              <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                No contests in this cycle yet — contests are the races voters mark, instantiated
                from the office catalog.
              </p>
            ) : (
              <ul className="divide-y divide-border-light dark:divide-border-dark">
                {(contests.data ?? []).map((contest) => (
                  <li key={contest.id} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
                    <span className="flex-1 text-text-primary dark:text-text-dark-primary">
                      {contest.display_label || contest.office_key}
                      <span className="ml-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        {contest.election_type}
                        {contest.party ? ` · ${contest.party}` : ''}
                        {' · '}
                        {contest.candidate_count} candidate{contest.candidate_count === 1 ? '' : 's'}
                      </span>
                    </span>
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        contest.status === 'official'
                          ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:bg-opacity-40 dark:text-success-300'
                          : 'bg-background-secondary text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary'
                      }`}
                    >
                      {contest.status}
                    </span>
                    {contest.status !== 'official' && (
                      <Button variant="ghost" size="sm" icon={CheckCircle}
                        ariaLabel={`Mark ${contest.display_label ?? 'contest'} official`}
                        loading={markOfficialMutation.isLoading && markOfficialMutation.variables === contest.id}
                        onClick={() => markOfficialMutation.mutate(contest.id)}>
                        Official
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" icon={Pencil} ariaLabel="Edit contest"
                      onClick={() => {
                        setContestForm({
                          id: contest.id,
                          office_id: contest.office_id,
                          electoral_district_id: '',
                          election_type: contest.election_type,
                          party: contest.party ?? '',
                          display_label: contest.display_label ?? '',
                          status: contest.status,
                        });
                        setContestModal('edit');
                      }} />
                    <Button variant="ghost" size="sm" icon={Trash2} ariaLabel="Delete contest"
                      onClick={() =>
                        setDeleteContestTarget({
                          id: contest.id,
                          label: contest.display_label || contest.office_key || 'this contest',
                        })
                      } />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <CandidateProfileCoverageCard electionYear={cycle.election_year} />
        </div>
      )}

      {tab === 'results' && calendarId && (
        <ElectionResultsTab calendarId={calendarId} electionYear={cycle.election_year} />
      )}

      {tab === 'activity' && calendarId && (
        <EntityAuditList entityType="election_calendar" entityId={calendarId} />
      )}

      <AdminModal
        isOpen={editOpen}
        title="Edit Election Cycle"
        onClose={() => setEditOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm" loading={editMutation.isLoading} onClick={() => editMutation.mutate()}>
              Save Changes
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label htmlFor="edit-cycle-name" className={labelClasses}>Display Name</label>
            <input id="edit-cycle-name" type="text" value={editForm.name}
              onChange={(event) => setEditForm((current) => ({ ...current, name: event.target.value }))}
              className="input" />
          </div>
          <div>
            <label htmlFor="edit-cycle-type" className={labelClasses}>Type</label>
            <select id="edit-cycle-type" value={editForm.election_type}
              onChange={(event) => setEditForm((current) => ({ ...current, election_type: event.target.value }))}
              className="input">
              {CYCLE_ELECTION_TYPES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="edit-cycle-start" className={labelClasses}>Cycle Window Start</label>
              <input id="edit-cycle-start" type="date" value={editForm.cycle_start_date}
                onChange={(event) => setEditForm((current) => ({ ...current, cycle_start_date: event.target.value }))}
                className="input" />
            </div>
            <div>
              <label htmlFor="edit-cycle-end" className={labelClasses}>Cycle Window End</label>
              <input id="edit-cycle-end" type="date" value={editForm.cycle_end_date}
                onChange={(event) => setEditForm((current) => ({ ...current, cycle_end_date: event.target.value }))}
                className="input" />
            </div>
          </div>
          <div>
            <label htmlFor="edit-cycle-blue-book" className={labelClasses}>Blue Book URL</label>
            <input id="edit-cycle-blue-book" type="url" value={editForm.blue_book_url}
              placeholder="https://leg.colorado.gov/…"
              onChange={(event) => setEditForm((current) => ({ ...current, blue_book_url: event.target.value }))}
              className="input" />
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              The state's ballot information booklet for this election. Every measure in the cycle links to it
              unless the measure has its own Blue Book URL. Leave blank to remove.
            </p>
          </div>
          <div>
            <label htmlFor="edit-cycle-notes" className={labelClasses}>Notes</label>
            <textarea id="edit-cycle-notes" rows={3} value={editForm.notes}
              onChange={(event) => setEditForm((current) => ({ ...current, notes: event.target.value }))}
              className="input" />
          </div>
        </div>
      </AdminModal>

      <AdminModal
        isOpen={contestModal !== null}
        title={contestModal === 'add' ? 'Add Contest' : 'Edit Contest'}
        onClose={() => setContestModal(null)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setContestModal(null)}>Cancel</Button>
            <Button variant="primary" size="sm"
              disabled={
                contestModal === 'add' &&
                (contestForm.office_id === '' ||
                  (selectedOffice?.scope === 'by_district' && contestForm.electoral_district_id === ''))
              }
              loading={contestSaveMutation.isLoading} onClick={() => contestSaveMutation.mutate()}>
              Save Contest
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {contestModal === 'add' && (
            <>
              <div>
                <label htmlFor="contest-office" className={labelClasses}>Office *</label>
                <select id="contest-office" value={contestForm.office_id}
                  onChange={(event) =>
                    setContestForm((current) => ({ ...current, office_id: event.target.value, electoral_district_id: '' }))
                  }
                  className="input">
                  <option value="">Select an office…</option>
                  {(offices.data ?? []).map((office) => (
                    <option key={office.id} value={office.id}>{office.display_label}</option>
                  ))}
                </select>
              </div>
              {selectedOffice?.scope === 'by_district' && (
                <div>
                  <label htmlFor="contest-district" className={labelClasses}>District *</label>
                  <select id="contest-district" value={contestForm.electoral_district_id}
                    onChange={(event) =>
                      setContestForm((current) => ({ ...current, electoral_district_id: event.target.value }))
                    }
                    className="input">
                    <option value="">Select a district…</option>
                    {(districts.data ?? []).map((district) => (
                      <option key={district.id} value={district.id}>{district.display_label}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="contest-type" className={labelClasses}>Election Type</label>
              <select id="contest-type" value={contestForm.election_type}
                onChange={(event) => setContestForm((current) => ({ ...current, election_type: event.target.value }))}
                className="input">
                {CONTEST_ELECTION_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            {contestForm.election_type === 'primary' && (
              <div>
                <label htmlFor="contest-party" className={labelClasses}>Party</label>
                <select id="contest-party" value={contestForm.party}
                  onChange={(event) => setContestForm((current) => ({ ...current, party: event.target.value }))}
                  className="input">
                  <option value="">No party</option>
                  {CONTEST_PARTY_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="contest-label" className={labelClasses}>Display Label</label>
            <input id="contest-label" type="text" value={contestForm.display_label}
              placeholder="Defaults to office — district (party primary)"
              onChange={(event) => setContestForm((current) => ({ ...current, display_label: event.target.value }))}
              className="input" />
          </div>
          {contestModal === 'edit' && (
            <div>
              <label htmlFor="contest-status" className={labelClasses}>Status</label>
              <select id="contest-status" value={contestForm.status}
                onChange={(event) => setContestForm((current) => ({ ...current, status: event.target.value }))}
                className="input">
                {CONTEST_STATUSES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={deleteContestTarget !== null}
        title="Delete contest"
        message={
          deleteContestTarget
            ? `Delete "${deleteContestTarget.label}"? Its candidates are unlinked from the contest — never deleted.`
            : ''
        }
        confirmText="Delete"
        variant="danger"
        onConfirm={() => deleteContestTarget && contestDeleteMutation.mutate(deleteContestTarget.id)}
        onCancel={() => setDeleteContestTarget(null)}
      />
    </AdminPageShell>
  );
};

export default ElectionCycleDetailPage;
